
/**
 * Utilidades para manejar diferentes tipos de URLs de video
 */

export interface VideoInfo {
  type: 'youtube' | 'direct';
  id?: string;
  embedUrl?: string;
  originalUrl: string;
}

/**
 * Extrae el ID de video de una URL de YouTube
 */
export const extractYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

/**
 * Determina el tipo de video y genera la información necesaria
 */
export const getVideoInfo = (url: string): VideoInfo => {
  if (!url) {
    return {
      type: 'direct',
      originalUrl: url
    };
  }

  const youtubeId = extractYouTubeVideoId(url);
  
  if (youtubeId) {
    return {
      type: 'youtube',
      id: youtubeId,
      embedUrl: `https://www.youtube.com/embed/${youtubeId}`,
      originalUrl: url
    };
  }

  return {
    type: 'direct',
    originalUrl: url
  };
};

/**
 * Valida si una URL es una URL de video válida
 */
export const isValidVideoUrl = (url: string): boolean => {
  if (!url) return false;
  
  // Verificar YouTube
  if (extractYouTubeVideoId(url)) {
    return true;
  }
  
  // Verificar URLs directas de video
  const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
  const hasVideoExtension = videoExtensions.some(ext => 
    url.toLowerCase().includes(ext)
  );
  
  // Verificar si es una URL HTTP/HTTPS válida
  try {
    const urlObj = new URL(url);
    return (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') || hasVideoExtension;
  } catch {
    return hasVideoExtension;
  }
};
